import { useEffect, useMemo, useState } from 'react'
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Platform, Modal, Dimensions, KeyboardAvoidingView, LayoutAnimation } from 'react-native'
import { useRouter } from 'expo-router'
import Constants from 'expo-constants'
import * as Device from 'expo-device'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import Ionicons from '@expo/vector-icons/Ionicons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import { showConfirm } from '../../lib/platform'
import { colors, fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import GradientButton from '../../components/GradientButton'
import PersonAvatarLab from '../../components/PersonAvatarLab'
import { useShowAvatars, setShowAvatars } from '../../lib/avatarPref'
import { Card, SectionLabel, SubHeader } from '../../components/ui'
import OnboardingOverlay from '../../components/Home/OnboardingOverlay'
import { Linking } from 'react-native'
import { WebView } from 'react-native-webview'

// ─── UI Helpers ──────────────────────────────────────────────────────
function AlertMsg({ type, children }) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const c = {
    info: { bg: 'rgba(59,130,246,.1)', border: 'rgba(59,130,246,.3)', color: '#7cb9ff' },
    error: { bg: colors.errorBg, border: tc.rosaBorder, color: tc.rosaText },
    success: { bg: 'rgba(16,185,129,.1)', border: 'rgba(16,185,129,.3)', color: '#10B981' },
  }[type] ?? {}
  return (
    <View style={[styles.alert, { backgroundColor: c.bg, borderColor: c.border }]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        {type === 'success' && <Ionicons name="checkmark-circle-outline" size={15} color="#10B981" />}
        {type === 'error' && <Ionicons name="close-circle-outline" size={15} color={tc.rosaText} />}
        <Text style={[styles.alertText, { color: c.color, flex: 1 }]}>{children}</Text>
      </View>
    </View>
  )
}

function BtnPrimary({ onPress, disabled, loading, children }) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  return (
    <TouchableOpacity
      style={[styles.btnPrimary, { backgroundColor: (disabled || loading) ? tc.bgCard2 : tc.rosa }]}
      onPress={onPress}
      disabled={disabled || loading}
    >
      <Text style={[styles.btnPrimaryText, { color: (disabled || loading) ? tc.textDim : tc.brandText }]}>
        {loading ? '...' : children}
      </Text>
    </TouchableOpacity>
  )
}

function SegmentControl({ options, selected, onSelect }) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  return (
    <View style={styles.segmentRow}>
      {options.map(o => (
        <TouchableOpacity
          key={o.key}
          onPress={() => onSelect(o.key)}
          style={[styles.segmentBtn, { backgroundColor: selected === o.key ? tc.rosa : tc.bgCard2, borderColor: selected === o.key ? tc.rosa : tc.border }]}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
            {o.icon}
            <Text style={[styles.segmentText, { color: selected === o.key ? tc.brandText : tc.textMuted }]}>{o.label}</Text>
          </View>
        </TouchableOpacity>
      ))}
    </View>
  )
}

function StyledInput({ value, onChangeText, placeholder, secureTextEntry, autoCapitalize, autoCorrect }) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  return (
    <TextInput
      style={[styles.input, { backgroundColor: tc.bgCard2, borderColor: tc.border, color: tc.text }]}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor={tc.textDim}
      secureTextEntry={secureTextEntry}
      autoCapitalize={autoCapitalize ?? 'none'}
      autoCorrect={autoCorrect ?? false}
    />
  )
}


// ─── 2. TEMA ─────────────────────────────────────────────────────────
function SectionTema({ theme, updateTheme }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  return (
    <Card>
      <SubHeader icon={<Ionicons name="color-palette-outline" size={16} color={tc.rosaText} />}>{t('profile.theme_title')}</SubHeader>
      <SegmentControl
        options={[
          { key: 'dark', label: t('profile.theme_dark'), icon: <Ionicons name="moon-outline" size={14} color={theme === 'dark' ? tc.brandText : tc.textMuted} /> },
          { key: 'light', label: t('profile.theme_light'), icon: <Ionicons name="sunny-outline" size={14} color={theme === 'light' ? tc.brandText : tc.textMuted} /> },
        ]}
        selected={theme}
        onSelect={updateTheme}
      />
      <Text style={[styles.subNote, { color: tc.textMuted }]}>{t('profile.theme_sub')}</Text>
    </Card>
  )
}

// ─── 3. LINGUA ───────────────────────────────────────────────────────
const LANGUAGES = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'da', label: 'Dansk', flag: '🇩🇰' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'no', label: 'Norsk', flag: '🇳🇴' },
  { code: 'sl', label: 'Slovenščina', flag: '🇸🇮' },
]

function SectionLingua({ userId }) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { t } = useTranslation()
  const insets = useSafeAreaInsets()
  const currentLang = i18n.language?.substring(0, 2) ?? 'it'
  const [selected, setSelected] = useState(currentLang)
  const [open, setOpen] = useState(false)
  const [msg, setMsg] = useState(null)
  const [loading, setLoading] = useState(false)

  const selectedLang = LANGUAGES.find(l => l.code === selected) ?? LANGUAGES[0]

  const handleSave = async () => {
    setLoading(true); setMsg(null)
    try {
      await supabase.from('dim_user').update({ language: selected }).eq('id_user', userId)
      i18n.changeLanguage(selected)
      setMsg({ type: 'success', text: t('profile.save_lang') })
    } catch { setMsg({ type: 'error', text: t('common.error_title') }) }
    finally { setLoading(false) }
  }

  const langItems = LANGUAGES.map((lang, i) => (
    <TouchableOpacity
      key={lang.code}
      style={[styles.langItem, { borderBottomColor: tc.border }, i === LANGUAGES.length - 1 && { borderBottomWidth: 0 }, lang.code === selected && { backgroundColor: tc.rosaSubtle }]}
      onPress={() => { setSelected(lang.code); setOpen(false) }}
    >
      <Text style={styles.langItemFlag}>{lang.flag}</Text>
      <Text style={[styles.langItemText, { color: lang.code === selected ? tc.rosaText : tc.text }]}>{lang.label}</Text>
      {lang.code === selected && <Ionicons name="checkmark" size={16} color={tc.rosaText} />}
    </TouchableOpacity>
  ))

  return (
    <>
      {Platform.OS !== 'web' && (
        <Modal visible={open} transparent animationType="slide" onRequestClose={() => setOpen(false)}>
          <View style={{ flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.55)' }}>
            <TouchableOpacity style={StyleSheet.absoluteFillObject} activeOpacity={1} onPress={() => setOpen(false)} />
            <View style={[styles.langSheet, { backgroundColor: tc.bgCard, borderColor: tc.border }]}>
              <View style={styles.langHandle} />
              <Text style={[styles.langSheetTitle, { color: tc.text, borderBottomColor: tc.border }]}>{t('profile.lang_title')}</Text>
              <ScrollView
                bounces={false}
                keyboardShouldPersistTaps="handled"
                style={{ maxHeight: Dimensions.get('window').height * 0.6 }}
                contentContainerStyle={{ paddingBottom: insets.bottom + 32 }}
              >
                {langItems}
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}
      <Card>
        <SubHeader icon={<Ionicons name="globe-outline" size={16} color={tc.rosaText} />}>{t('profile.lang_title')}</SubHeader>
        <TouchableOpacity
          style={[styles.langTrigger, { backgroundColor: tc.bgCard2, borderColor: open ? tc.rosa : tc.border }]}
          onPress={() => setOpen(o => !o)}
          activeOpacity={0.8}
        >
          <Text style={styles.langTriggerFlag}>{selectedLang.flag}</Text>
          <Text style={[styles.langTriggerText, { color: tc.text }]}>{selectedLang.label}</Text>
          <Ionicons name={open && Platform.OS === 'web' ? 'chevron-up' : 'chevron-down'} size={16} color={tc.textMuted} />
        </TouchableOpacity>
        {Platform.OS === 'web' && open && (
          <View style={[styles.langWebList, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
            {langItems}
          </View>
        )}
        {msg && <AlertMsg type={msg.type}>{msg.text}</AlertMsg>}
        <GradientButton onPress={handleSave} loading={loading} disabled={selected === currentLang} style={{ width: '100%', marginTop: 12 }}>
          {t('profile.save_lang')}
        </GradientButton>
      </Card>
    </>
  )
}

// ─── IDENTITÀ (nickname + avatar) ────────────────────────────────────
// Come su VB: il nickname sta in cima alla stessa card dell'avatar. L'editor del nuovo avatar
// persona e' aperto a tutti.
function SectionIdentita({ userId, currentNickname, onNicknameUpdate, showAvatar }) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { t } = useTranslation()
  const [value, setValue] = useState(currentNickname ?? '')
  const [msg, setMsg] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => { setValue(currentNickname ?? '') }, [currentNickname])

  const handleSave = async () => {
    if (!value.trim()) return
    setLoading(true); setMsg(null)
    try {
      const { data: available } = await supabase.rpc('fn_check_nickname', { p_nickname: value, p_id_user: userId })
      if (!available) { setMsg({ type: 'error', text: t('profile.nick_taken') }); setLoading(false); return }
      await supabase.from('dim_user').update({ nickname: value, display_name: value }).eq('id_user', userId)
      setMsg({ type: 'success', text: t('profile.nick_success') })
      onNicknameUpdate(value)
    } catch { setMsg({ type: 'error', text: t('common.error_data') }) }
    finally { setLoading(false) }
  }

  return (
    <Card>
      <Text style={[styles.fieldLabel, { color: tc.textMuted }]}>{t('profile.nick')}</Text>
      <StyledInput value={value} onChangeText={setValue} placeholder={t('profile.nick_placeholder')} />
      {msg && <AlertMsg type={msg.type}>{msg.text}</AlertMsg>}
      <GradientButton onPress={handleSave} loading={loading} disabled={!value.trim() || value === currentNickname} style={{ width: '100%' }}>{t('profile.update_btn')}</GradientButton>
      {showAvatar && (
        <>
          <View style={[styles.divider, { backgroundColor: tc.border, marginVertical: 16 }]} />
          <PersonAvatarLab />
        </>
      )}
    </Card>
  )
}

// ─── PASSWORD ────────────────────────────────────────────────────────
// Azione rara: collassata in una riga, il form si apre solo al tocco (come su VB).
function SectionPassword() {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const [open, setOpen] = useState(false)
  const [newPass, setNewPass] = useState('')
  const [confirmPass, setConfirm] = useState('')
  const [msg, setMsg] = useState(null)
  const [loading, setLoading] = useState(false)
  const [show, setShow] = useState(false)

  const animateToggle = (next) => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut)
    setOpen(next)
  }

  const handleCancel = () => {
    setNewPass(''); setConfirm(''); setMsg(null)
    animateToggle(false)
  }

  const handleSave = async () => {
    if (newPass.length < 6) { setMsg({ type: 'error', text: t('profile.pass_min_length') }); return }
    if (newPass !== confirmPass) { setMsg({ type: 'error', text: t('profile.pass_mismatch') }); return }
    setLoading(true); setMsg(null)
    try {
      const { error } = await supabase.auth.updateUser({ password: newPass })
      if (error) throw error
      setMsg({ type: 'success', text: t('profile.security_success') })
      setNewPass(''); setConfirm('')
    } catch (e) { setMsg({ type: 'error', text: e.message }) }
    finally { setLoading(false) }
  }

  if (!open) {
    return (
      <Card>
        <TouchableOpacity style={styles.collapsedRow} onPress={() => animateToggle(true)} accessibilityRole="button">
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Ionicons name="key-outline" size={16} color={tc.text} />
            <Text style={[styles.collapsedLabel, { color: tc.text }]}>{t('profile.security_title')}</Text>
          </View>
          <Text style={[styles.collapsedAction, { color: tc.rosaText }]}>{t('profile.change_pass')}</Text>
        </TouchableOpacity>
      </Card>
    )
  }

  return (
    <Card>
      <SubHeader icon={<Ionicons name="key-outline" size={16} color={tc.rosaText} />}>{t('profile.security_title')}</SubHeader>
      <StyledInput value={newPass} onChangeText={setNewPass} placeholder={t('profile.new_pass_placeholder')} secureTextEntry={!show} />
      <StyledInput value={confirmPass} onChangeText={setConfirm} placeholder={t('profile.confirm_password')} secureTextEntry={!show} />
      <TouchableOpacity style={styles.checkRow} onPress={() => setShow(s => !s)}>
        <View style={[styles.checkbox, { borderColor: show ? tc.rosa : tc.border, backgroundColor: show ? tc.rosa : 'transparent' }]}>
          {show && <Ionicons name="checkmark" size={12} color={tc.brandText} />}
        </View>
        <Text style={[styles.checkLabel, { color: tc.textMuted }]}>{t('profile.show_pass')}</Text>
      </TouchableOpacity>
      {msg && <AlertMsg type={msg.type}>{msg.text}</AlertMsg>}
      <GradientButton onPress={handleSave} loading={loading} disabled={!newPass || !confirmPass} style={{ width: '100%' }}>{t('profile.update_pass')}</GradientButton>
      <TouchableOpacity onPress={handleCancel} style={{ marginTop: 10 }}>
        <Text style={[styles.cancelLink, { color: tc.text }]}>{t('profile.cancel')}</Text>
      </TouchableOpacity>
    </Card>
  )
}

// ─── LE MIE LEGHE ────────────────────────────────────────────────────
// Oltre COLLAPSED_COUNT leghe la lista si accorcia; la lega predefinita sta sempre in cima.
const COLLAPSED_COUNT = 3

function SectionLeghe({ userId }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { userLeagues, leagueId, switchLeague, setDefaultLeague } = useAuth()
  const [updating, setUpdating] = useState(null)
  const [msg, setMsg] = useState(null)
  const [expanded, setExpanded] = useState(false)

  const handleSetDefault = async (newLeagueId) => {
    setUpdating(newLeagueId); setMsg(null)
    try {
      await setDefaultLeague(newLeagueId)
      await switchLeague(newLeagueId)
      setMsg({ type: 'success', text: t('profile.league_default_updated') })
    } catch { setMsg({ type: 'error', text: t('common.error_title') }) }
    finally { setUpdating(null) }
  }

  const handleLeave = async (l) => {
    const confirmed = await showConfirm(t('profile.league_leave_confirm', { name: l.name }))
    if (!confirmed) return
    try {
      const { data, error } = await supabase.rpc('fn_leave_league', { p_id_user: userId, p_id_league: l.id_league })
      if (error || !data?.[0]?.success) setMsg({ type: 'error', text: data?.[0]?.message ?? t('common.error_title') })
      else setMsg({ type: 'success', text: t('profile.league_left') })
    } catch (e) { setMsg({ type: 'error', text: e.message }) }
  }

  const sorted = useMemo(() => {
    const active = userLeagues.filter(l => l.id_league === leagueId)
    const rest = userLeagues.filter(l => l.id_league !== leagueId)
    return [...active, ...rest]
  }, [userLeagues, leagueId])
  const visible = expanded ? sorted : sorted.slice(0, COLLAPSED_COUNT)
  const hiddenCount = sorted.length - visible.length

  return (
    <Card>
      {msg && <AlertMsg type={msg.type}>{msg.text}</AlertMsg>}
      <View style={{ gap: 8 }}>
        {visible.map(l => {
          const isDefault = l.id_league === leagueId
          return (
            <View key={l.id_league} style={[styles.leagueRow, {
              backgroundColor: isDefault ? tc.rosaSubtle : tc.bgCard2,
              borderColor: isDefault ? tc.rosa : tc.border,
            }]}>
              <View style={{ flex: 1 }}>
                <Text style={[styles.leagueName, { color: isDefault ? tc.rosaText : tc.text }]}>{l.name}</Text>
                <Text style={[styles.leagueMeta, { color: tc.textMuted }]}>{t('profile.members_count', { count: l.member_count })}</Text>
              </View>
              <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center' }}>
                {isDefault ? (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                    <Ionicons name="checkmark-circle" size={14} color={tc.rosaText} />
                    <Text style={[styles.defaultBadge, { color: tc.rosaText }]}>{t('profile.default_badge')}</Text>
                  </View>
                ) : (
                  <TouchableOpacity style={[styles.leagueActionBtn, { borderColor: tc.border }]} onPress={() => handleSetDefault(l.id_league)} disabled={!!updating}>
                    <Text style={[styles.leagueActionText, { color: tc.textMuted }]}>{t('profile.set_default')}</Text>
                  </TouchableOpacity>
                )}
                {!isDefault && (
                  <TouchableOpacity style={[styles.leagueActionBtn, { borderColor: tc.rosaBorder }]} onPress={() => handleLeave(l)}>
                    <Text style={[styles.leagueActionText, { color: tc.rosaText }]}>{t('profile.leave')}</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          )
        })}
      </View>
      {sorted.length > COLLAPSED_COUNT && (
        <TouchableOpacity style={styles.showMoreBtn} onPress={() => setExpanded(e => !e)}>
          <Text style={[styles.showMoreText, { color: tc.textMuted }]}>
            {expanded ? t('profile.show_less') : t('profile.show_more', { count: hiddenCount })}
          </Text>
          <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={14} color={tc.textMuted} />
        </TouchableOpacity>
      )}
    </Card>
  )
}

// ─── NOTIFICHE ────────────────────────────────────────────────────
function SectionNotifiche({ userId }) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { t } = useTranslation()

  const [prefs, setPrefs] = useState({
    notif_enabled: true,
    notif_results: true,
    notif_startlist: true,
    notif_reminder: 'always',
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => {
    if (!userId) return
    supabase
      .from('dim_user')
      .select('notif_enabled, notif_results, notif_startlist, notif_reminder')
      .eq('id_user', userId)
      .single()
      .then(({ data }) => {
        if (data) setPrefs(data)
        setLoading(false)
      })
  }, [userId])

  const save = async (updated) => {
    setSaving(true); setMsg(null)
    try {
      const { error } = await supabase.from('dim_user').update(updated).eq('id_user', userId)
      if (error) throw error
      setPrefs(updated)
      setMsg({ type: 'success', text: t('profile.notif_saved') })
    } catch {
      setMsg({ type: 'error', text: t('profile.notif_error') })
    } finally { setSaving(false) }
  }

  const toggle = (field) => save({ ...prefs, [field]: !prefs[field] })
  const setReminder = (value) => save({ ...prefs, notif_reminder: value })

  if (loading) return null

  const isDisabled = !prefs.notif_enabled

  return (
    <Card>
      <View style={[stylesNotif.row, { borderBottomColor: tc.border }]}>
        <View style={{ flex: 1 }}>
          <Text style={[stylesNotif.rowLabel, { color: tc.text }]}>{t('profile.notif_enabled')}</Text>
          <Text style={[stylesNotif.rowSub, { color: tc.textMuted }]}>{t('profile.notif_enabled_sub')}</Text>
        </View>
        <Toggle value={prefs.notif_enabled} onToggle={() => toggle('notif_enabled')} tc={tc} />
      </View>
      <View style={[stylesNotif.row, { borderBottomColor: tc.border, opacity: isDisabled ? 0.4 : 1 }]}>
        <View style={{ flex: 1 }}>
          <Text style={[stylesNotif.rowLabel, { color: tc.text }]}>{t('profile.notif_results')}</Text>
          <Text style={[stylesNotif.rowSub, { color: tc.textMuted }]}>{t('profile.notif_results_sub')}</Text>
        </View>
        <Toggle value={prefs.notif_results} onToggle={() => !isDisabled && toggle('notif_results')} tc={tc} />
      </View>
      <View style={[stylesNotif.row, { borderBottomColor: tc.border, opacity: isDisabled ? 0.4 : 1 }]}>
        <View style={{ flex: 1 }}>
          <Text style={[stylesNotif.rowLabel, { color: tc.text }]}>{t('profile.notif_startlist')}</Text>
          <Text style={[stylesNotif.rowSub, { color: tc.textMuted }]}>{t('profile.notif_startlist_sub')}</Text>
        </View>
        <Toggle value={prefs.notif_startlist} onToggle={() => !isDisabled && toggle('notif_startlist')} tc={tc} />
      </View>
      <View style={{ opacity: isDisabled ? 0.4 : 1, marginTop: 4 }}>
        <Text style={[styles.fieldLabel, { color: tc.textMuted }]}>{t('profile.notif_reminder')}</Text>
        <Text style={[stylesNotif.rowSub, { color: tc.textMuted, marginBottom: 10 }]}>{t('profile.notif_reminder_sub')}</Text>
        <SegmentControl
          options={[
            { key: 'off', label: t('profile.notif_reminder_off') },
            { key: 'only_if_no_pick', label: t('profile.notif_reminder_no_pick') },
            { key: 'always', label: t('profile.notif_reminder_always') },
          ]}
          selected={prefs.notif_reminder}
          onSelect={(v) => !isDisabled && setReminder(v)}
        />
      </View>
      {saving && <Text style={[stylesNotif.rowSub, { color: tc.textMuted, marginTop: 8, textAlign: 'center' }]}>{t('profile.notif_saving')}</Text>}
      {msg && <AlertMsg type={msg.type}>{msg.text}</AlertMsg>}
    </Card>
  )
}

// ─── AVATAR (interruttore) ─────────────────────────────────────────
// Salvato sul telefono (lib/avatarPref.js), non sull'account. Spento = corridori con la maglia, giocatori solo col nome; il proprio avatar in Home resta.
function SectionAvatar() {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const enabled = useShowAvatars()
  return (
    <Card>
      <View style={[stylesNotif.row, { borderBottomWidth: 0, marginBottom: 0 }]}>
        <View style={{ flex: 1 }}>
          <Text style={[stylesNotif.rowLabel, { color: tc.text }]}>{t('profile.avatars')}</Text>
          <Text style={[stylesNotif.rowSub, { color: tc.textMuted }]}>{t('profile.avatars_sub')}</Text>
        </View>
        <Toggle value={enabled} onToggle={() => setShowAvatars(!enabled)} tc={tc} />
      </View>
    </Card>
  )
}

function Toggle({ value, onToggle, tc }) {
  return (
    <TouchableOpacity
      onPress={onToggle}
      style={[stylesNotif.toggle, { backgroundColor: value ? tc.rosa : tc.bgCard2, borderColor: value ? tc.rosa : tc.border }]}
      activeOpacity={0.8}
    >
      <View style={[stylesNotif.toggleThumb, { transform: [{ translateX: value ? 18 : 2 }] }]} />
    </TouchableOpacity>
  )
}


// ─── CONTATTI (tour, regolamento, segnalazioni, voto, privacy) ───────
// Stessa struttura a righe di VB. Bug e feedback usano lo stesso modale e lo stesso backend
// (dim_bug_report.kind + email via Edge Function send-bug-report).
function InfoRow({ icon, label, onPress, first }) {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  return (
    <>
      {!first && <View style={[styles.divider, { backgroundColor: tc.border, marginTop: 10 }]} />}
      <TouchableOpacity style={styles.collapsedRow} onPress={onPress} accessibilityRole="button" accessibilityLabel={label}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Ionicons name={icon} size={16} color={tc.text} />
          <Text style={[styles.collapsedLabel, { color: tc.text }]}>{label}</Text>
        </View>
        <Ionicons name="chevron-forward" size={16} color={tc.textMuted} />
      </TouchableOpacity>
    </>
  )
}

function SectionInfo({ userId, nickname, userEmail }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const router = useRouter()
  const insets = useSafeAreaInsets()
  const [rulesOpen, setRulesOpen] = useState(false)
  const [tourOpen, setTourOpen] = useState(false)
  const [bugOpen, setBugOpen] = useState(false)
  const [reportKind, setReportKind] = useState('bug') // 'bug' | 'feedback'
  const [bugMsg, setBugMsg] = useState('')
  const [sending, setSending] = useState(false)
  const [bugResult, setBugResult] = useState(null)

  // Allegato a ogni segnalazione: aiuta a diagnosticare problemi legati al dispositivo
  const deviceInfo = `${Device.modelName ?? Platform.OS} · ${Device.osName ?? Platform.OS} ${Device.osVersion ?? ''} · App v${Constants.expoConfig?.version ?? '?'}`

  const handleSendReport = async () => {
    const text = bugMsg.trim()
    if (!text) return
    setSending(true); setBugResult(null)
    try {
      // 1) Tabella (fonte di verita)
      const { error: insertError } = await supabase.from('dim_bug_report').insert({
        id_user: userId, nickname, message: text, device_info: deviceInfo, kind: reportKind,
      })
      if (insertError) console.error('[SectionInfo] salvataggio dim_bug_report fallito:', insertError.message)
      // 2) Email di notifica: se fallisce il messaggio e' comunque salvato
      const { error: fnError } = await supabase.functions.invoke('send-bug-report', {
        body: { message: text, nickname, user_email: userEmail, device_info: deviceInfo, kind: reportKind },
      })
      if (fnError) console.error('[SectionInfo] send-bug-report fallito:', fnError.message)
      if (insertError && fnError) throw new Error('messaggio non consegnato')
      setBugResult({ type: 'success', text: reportKind === 'feedback' ? t('profile.feedback_sent') : t('profile.bug_sent') })
      setBugMsg('')
      setTimeout(() => { setBugOpen(false); setBugResult(null) }, 1200)
    } catch {
      setBugResult({ type: 'error', text: t('common.error_generic') })
    } finally { setSending(false) }
  }

  // Pagina di recensione dello store (link fisso, senza i limiti della finestra di sistema)
  const openStoreReview = async () => {
    const [native, web] = Platform.OS === 'ios'
      ? ['itms-apps://apps.apple.com/app/id6503638356?action=write-review', 'https://apps.apple.com/app/id6503638356?action=write-review']
      : ['market://details?id=com.virtuacycling', 'https://play.google.com/store/apps/details?id=com.virtuacycling']
    try { await Linking.openURL(native) } catch { Linking.openURL(web) }
  }

  const openReport = (kind) => {
    setReportKind(kind); setBugOpen(true); setBugResult(null); setBugMsg('')
  }

  const reportTitle = reportKind === 'feedback' ? t('profile.leave_feedback') : t('profile.report_bug')

  return (
    <>
      <Modal visible={rulesOpen} animationType="slide" onRequestClose={() => setRulesOpen(false)}>
        <View style={{ flex: 1, backgroundColor: tc.bg, paddingTop: insets.top }}>
          <View style={styles.sheetHeader}>
            <Text style={[styles.sheetTitle, { color: tc.text }]}>{t('profile.rules')}</Text>
            <TouchableOpacity onPress={() => setRulesOpen(false)} style={[styles.closeBtn, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
              <Ionicons name="close" size={16} color={tc.textMuted} />
            </TouchableOpacity>
          </View>
          <WebView source={{ uri: 'https://www.virtuacycling.com/rules.html' }} style={{ flex: 1 }} />
        </View>
      </Modal>

      <Modal visible={bugOpen} transparent animationType="slide" onRequestClose={() => setBugOpen(false)}>
        <KeyboardAvoidingView style={{ flex: 1, justifyContent: 'flex-end' }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <TouchableOpacity style={{ ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(0,0,0,0.6)' }} activeOpacity={1} onPress={() => setBugOpen(false)} />
          <View style={{ backgroundColor: tc.bgCard, borderTopLeftRadius: 20, borderTopRightRadius: 20, borderWidth: 1, borderColor: tc.border, padding: 20, paddingBottom: insets.bottom + 20 }}>
            <View style={[styles.sheetHeader, { paddingHorizontal: 0, borderBottomWidth: 0, paddingTop: 0 }]}>
              <Text style={[styles.sheetTitle, { color: tc.text }]}>{reportTitle}</Text>
              <TouchableOpacity onPress={() => setBugOpen(false)} style={[styles.closeBtn, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}>
                <Ionicons name="close" size={16} color={tc.textMuted} />
              </TouchableOpacity>
            </View>
            <TextInput
              style={[styles.bugInput, { backgroundColor: tc.bgCard2, borderColor: tc.border, color: tc.text }]}
              placeholder={reportKind === 'feedback' ? t('profile.feedback_placeholder') : t('profile.bug_placeholder')}
              accessibilityLabel={reportTitle}
              maxLength={4000}
              placeholderTextColor={tc.textDim}
              value={bugMsg}
              onChangeText={setBugMsg}
              multiline
              autoCapitalize="sentences"
            />
            {bugResult && <AlertMsg type={bugResult.type}>{bugResult.text}</AlertMsg>}
            <GradientButton onPress={handleSendReport} loading={sending} disabled={!bugMsg.trim() || !!bugResult} style={{ width: '100%' }}>
              {t('common.send')}
            </GradientButton>
          </View>
        </KeyboardAvoidingView>
      </Modal>

      {tourOpen && <OnboardingOverlay userId={userId} replay onDone={() => setTourOpen(false)} />}

      <Card>
        <InfoRow first icon="play-circle-outline" label={t('profile.replay_tour')} onPress={() => setTourOpen(true)} />
        <InfoRow icon="book-outline" label={t('profile.rules')} onPress={() => setRulesOpen(true)} />
        <InfoRow icon="bug-outline" label={t('profile.report_bug')} onPress={() => openReport('bug')} />
        <InfoRow icon="bulb-outline" label={t('profile.leave_feedback')} onPress={() => openReport('feedback')} />
        {Platform.OS !== 'web' && <InfoRow icon="star-outline" label={t('profile.rate_app')} onPress={openStoreReview} />}
        <InfoRow icon="lock-closed-outline" label="Privacy Policy" onPress={() => router.push('/privacy')} />
      </Card>
    </>
  )
}

// ─── ZONA PERICOLOSA (logout + elimina account) ──────────────────────
function SectionZonaPericolosa({ userId, signOut }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [msg, setMsg] = useState(null)

  const handleDelete = () => {
    showConfirm(t('profile.delete_confirm')).then(async (confirmed) => {
      if (!confirmed) return
      setLoading(true); setMsg(null)
      try {
        const { error } = await supabase.rpc('fn_delete_user', { p_id_user: userId })
        if (error) throw error
        await signOut()
      } catch (e) {
        // la funzione blocca chi ha creato leghe con altri iscritti (eliminarlo cancellerebbe la lega di tutti)
        setMsg({ type: 'error', text: String(e?.message ?? '').includes('LEAGUE_OWNER') ? t('profile.delete_league_owner') : (e?.message ?? t('common.error_generic')) })
        setLoading(false)
      }
    })
  }

  // Esci e Elimina sono separati: Elimina sta sotto, lontano, come link testuale (non si tocca per errore al posto di Esci)
  return (
    <>
      <Card>
        <TouchableOpacity
          style={{ borderWidth: 1, borderColor: tc.border, borderRadius: 10, padding: 12, alignItems: 'center' }}
          onPress={signOut}
          accessibilityRole="button"
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Ionicons name="log-out-outline" size={16} color={tc.textMuted} />
            <Text style={{ color: tc.textMuted, fontFamily: fonts.condensedBold, fontSize: 15 }}>{t('common.logout')}</Text>
          </View>
        </TouchableOpacity>
      </Card>

      <View style={{ marginTop: 32, marginBottom: 16, alignItems: 'center' }}>
        <Text style={{ fontSize: 12, color: tc.textMuted, lineHeight: 18, textAlign: 'center', paddingHorizontal: 12 }}>{t('profile.delete_desc')}</Text>
        {msg && <AlertMsg type={msg.type}>{msg.text}</AlertMsg>}
        <TouchableOpacity
          style={{ minHeight: 44, minWidth: 44, paddingHorizontal: 18, paddingVertical: 12, marginTop: 4, justifyContent: 'center', opacity: loading ? 0.6 : 1 }}
          onPress={handleDelete}
          disabled={loading}
          accessibilityRole="button"
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Ionicons name="trash-outline" size={16} color="#EF4444" />
            <Text style={{ color: '#EF4444', fontFamily: fonts.condensedBold, fontSize: 15 }}>{loading ? '...' : t('profile.delete_btn')}</Text>
          </View>
        </TouchableOpacity>
      </View>
    </>
  )
}

const stylesNotif = StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 12, borderBottomWidth: 1, marginBottom: 4 },
  rowLabel: { fontSize: 14, fontWeight: '600', marginBottom: 2 },
  rowSub: { fontSize: 11, lineHeight: 15 },
  toggle: { width: 44, height: 26, borderRadius: 13, borderWidth: 1, justifyContent: 'center' },
  toggleThumb: { width: 20, height: 20, borderRadius: 10, backgroundColor: '#ffffff', shadowColor: '#000', shadowOpacity: 0.15, shadowRadius: 2, elevation: 2 },
})

// ─── EXPORT ──────────────────────────────────────────────────────────
// Rimando alla Bacheca (badge, maglie, albo d'oro) della lega attiva
function TrophyCaseLink() {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const router = useRouter()
  return (
    <TouchableOpacity
      onPress={() => router.push('/Leagues/badges')}
      activeOpacity={0.8}
      accessibilityRole="button"
      accessibilityLabel={`${t('badges.album_title')}. ${t('badges.trophy_sub')}`}
      style={{ flexDirection: 'row', alignItems: 'center', gap: 12, borderWidth: 1, borderRadius: 14, padding: 14, minHeight: 64, marginBottom: 20, backgroundColor: tc.bgCard, borderColor: tc.border }}
    >
      <MaterialCommunityIcons name="trophy-outline" size={26} color={tc.rosaText} />
      <View style={{ flex: 1 }}>
        <Text style={{ fontFamily: fonts.condensedBold, fontSize: 16, color: tc.text }}>{t('badges.album_title')}</Text>
        <Text style={{ fontSize: 12, color: tc.textMuted, marginTop: 2 }}>{t('badges.trophy_sub')}</Text>
      </View>
      <Ionicons name="chevron-forward" size={16} color={tc.textMuted} />
    </TouchableOpacity>
  )
}

export default function Profilo() {
  const { t } = useTranslation()
  const { user, nickname, theme, updateTheme, isAdmin, signOut } = useAuth()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const insets = useSafeAreaInsets()
  const [currentNickname, setCurrentNickname] = useState(nickname)

  const appVersion = Constants.expoConfig?.version ?? '—'
  const buildNumber = Constants.expoConfig?.ios?.buildNumber ?? Constants.expoConfig?.android?.versionCode ?? '—'

  useEffect(() => { setCurrentNickname(nickname) }, [nickname])

  return (
    <ScrollView style={[styles.container, { backgroundColor: tc.bg }]} contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 60 }]}>
      <SectionLabel mb={20}>{t('profile.settings')}</SectionLabel>
      <Text style={[styles.intro, { color: tc.textMuted }]}>
        {t('profile.hi')} <Text style={{ color: tc.rosaText, fontWeight: '700' }}>{currentNickname}</Text>, {t('profile.intro')}
      </Text>

      <SectionLabel mb={10}>{t('profile.group_identity')}</SectionLabel>
      <SectionIdentita userId={user?.id} currentNickname={currentNickname} onNicknameUpdate={setCurrentNickname} showAvatar />

      <TrophyCaseLink />

      <SectionLabel mb={10}>{t('profile.group_account')}</SectionLabel>
      <SectionTema theme={theme} updateTheme={updateTheme} />
      <SectionLingua userId={user?.id} />
      <SectionPassword />

      <SectionLabel mb={10}>{t('profile.my_leagues')}</SectionLabel>
      <SectionLeghe userId={user?.id} />

      <SectionLabel mb={10}>{t('profile.notif_title')}</SectionLabel>
      <SectionNotifiche userId={user?.id} />

      <SectionLabel mb={10}>{t('profile.avatars_title')}</SectionLabel>
      <SectionAvatar />

      <SectionLabel mb={10}>{t('profile.group_contact')}</SectionLabel>
      <SectionInfo userId={user?.id} nickname={currentNickname} userEmail={user?.email} />

      <SectionLabel mb={10}>{t('profile.group_danger')}</SectionLabel>
      <SectionZonaPericolosa userId={user?.id} signOut={signOut} />

      {isAdmin && (
        // area di tocco >= 44pt (iOS) / 48dp (Android)
        <TouchableOpacity
          style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, alignSelf: 'center', minHeight: 44, minWidth: 44, paddingHorizontal: 18, paddingVertical: 12, marginBottom: 12, marginTop: 4 }}
          onPress={() => Linking.openURL('https://admin.virtuacycling.com')}
          accessibilityRole="link"
          accessibilityLabel={t('profile.admin_panel')}
        >
          <Ionicons name="settings-outline" size={14} color={tc.rosaText} style={{ opacity: 0.7 }} />
          <Text style={{ fontSize: 12, fontWeight: '600', color: tc.rosaText, opacity: 0.7 }}>{t('profile.admin_panel')}</Text>
        </TouchableOpacity>
      )}

      <Text style={[styles.footer, { color: tc.textDim }]}>Virtua Cycling v{appVersion} ({buildNumber})</Text>
    </ScrollView>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container: { flex: 1 },
  content: { padding: 16 },
  intro: { fontSize: 13, marginBottom: 20 },
  alert: { borderWidth: 1, borderRadius: radius.md, padding: 10, paddingHorizontal: 14, marginBottom: 12 },
  alertText: { fontSize: 13, lineHeight: 18 },
  input: { borderWidth: 1, borderRadius: radius.lg, padding: 11, paddingHorizontal: 14, fontSize: 14, marginBottom: 12 },
  btnPrimary: { borderRadius: radius.lg, padding: 12, alignItems: 'center' },
  btnPrimaryText: { fontFamily: fonts.condensedBold, fontSize: 15, letterSpacing: 0.5 },
  segmentRow: { flexDirection: 'row', gap: 10, marginBottom: 12 },
  segmentBtn: { flex: 1, borderWidth: 1, borderRadius: radius.lg, padding: 12, alignItems: 'center', justifyContent: 'center' },
  segmentText: { fontFamily: fonts.condensedBold, fontSize: 13, textAlign: 'center' },
  langTrigger: { flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: 12, padding: 14, paddingHorizontal: 16, marginBottom: 4 },
  langTriggerFlag: { fontSize: 22 },
  langTriggerText: { flex: 1, fontSize: 16, fontWeight: '600' },
  langSheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, borderWidth: 1, maxHeight: '75%' },
  langHandle: { width: 36, height: 4, backgroundColor: 'rgba(150,150,150,0.4)', borderRadius: 2, alignSelf: 'center', marginTop: 10, marginBottom: 4 },
  langSheetTitle: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 1, padding: 14, paddingHorizontal: 16, borderBottomWidth: 1, textAlign: 'center' },
  langWebList: { borderWidth: 1, borderRadius: 12, overflow: 'hidden', marginTop: 4, marginBottom: 8 },
  langItem: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, paddingHorizontal: 16, borderBottomWidth: 1 },
  langItemFlag: { fontSize: 22 },
  langItemText: { flex: 1, fontSize: 15, fontWeight: '500' },
  subNote: { fontSize: 11, textAlign: 'center', marginTop: -4 },
  checkRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 },
  checkbox: { width: 18, height: 18, borderRadius: 4, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  checkLabel: { fontSize: 12 },
  leagueRow: { borderWidth: 1, borderRadius: radius.lg, padding: 10, paddingHorizontal: 14, flexDirection: 'row', alignItems: 'center' },
  leagueName: { fontSize: 13, fontWeight: '700' },
  leagueMeta: { fontSize: 10, marginTop: 2 },
  defaultBadge: { fontSize: 10, fontFamily: fonts.condensedBold },
  leagueActionBtn: { borderWidth: 1, borderRadius: radius.sm, paddingHorizontal: 10, paddingVertical: 4 },
  leagueActionText: { fontSize: 11, fontWeight: '600' },
  footer: { textAlign: 'center', fontSize: 11, marginBottom: 8, opacity: 0.5 },
  fieldLabel: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  divider: { height: 0.5, marginBottom: 14 },
  bugInput: { borderWidth: 1, borderRadius: radius.lg, padding: 14, fontSize: 14, minHeight: 120, textAlignVertical: 'top', marginBottom: 12 },
  sheetHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: 'rgba(150,150,150,0.2)', marginBottom: 4 },
  sheetTitle: { fontFamily: fonts.condensedBold, fontSize: 16, textTransform: 'uppercase' },
  closeBtn: { borderWidth: 1, borderRadius: 8, width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
  collapsedRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  collapsedLabel: { fontSize: 14, fontWeight: '600' },
  collapsedAction: { fontSize: 13, fontWeight: '700' },
  cancelLink: { textAlign: 'center', fontSize: 13, fontWeight: '600', textDecorationLine: 'underline' },
  showMoreBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 4, paddingVertical: 10, marginTop: 4 },
  showMoreText: { fontSize: 12, fontWeight: '600' },
})